/* eslint-disable @typescript-eslint/no-explicit-any */
import { Ref, ref, watch, onMounted, onUnmounted, defineComponent } from 'vue';
import {
  useUIStore,
  useNamespace,
  getEditorEmits,
  getMarkDownProps,
} from '@ibiz-template/vue3-util';
import { createUUID } from 'qx-util';
import Cherry from 'cherry-markdown';
import {
  CoreConst,
  IBizContext,
  getAppCookie,
  IChatMessage,
} from '@ibiz-template/core';
import {
  UIActionUtil,
  parseSearchParams,
  OpenAppViewCommand,
} from '@ibiz-template/runtime';
import { MarkDownEditorController } from '../markdown-editor.controller';
import { initCustomMenu } from './custom-menu';
import './ibiz-markdown-editor.scss';

/**
 * 移动端Markdown编辑框
 * @primary
 * @description 基于cherry-markdown深度定制可扩展的Markdown编辑器，用于Markdown文档编辑。支持编辑器类型包含：`移动端Markdown编辑框`
 * @editorparams {name:uploadparams,parameterType:string,description:上传参数，图片或文件上传时，用于计算上传路径}
 * @editorparams {name:exportparams,parameterType:string,description:下载参数，图片或文件下载时，用于计算下载路径}
 * @editorparams {name:osscat,parameterType:string,description:用于计算上传和下载路径的OSS参数}
 * @editorparams {name:readonly,parameterType:boolean,defaultvalue:false,description:设置编辑器是否为只读态}
 * @editorparams {"name":"srfaiappendcurdata","parameterType":"boolean","defaultvalue":false,"description":"在打开AI功能时，该参数用于判断是否传入对象参数，主要用于在请求历史记录时，附加当前参数"}
 * @editorparams {"name":"srfaiappendcurcontent","parameterType":"string","description":"在打开AI功能时，如果该参数存在值，会将其传入编辑内容作为用户消息，主要用于在请求历史记录后，附加当前编辑内容作为用户消息"}
 * @editorparams {"name":"autoquestion","parameterType":"boolean","defaultvalue":true,"description": "在打开AI功能时历史数据最后一个项是用户消息（USER）时是否自动提问，当打开AI行内聊天时是否自动提问"}
 * @editorparams {"name":"autofill","parameterType":"boolean","defaultvalue":false,"description": "用于AI聊天，AI回答完成之后是否触发回填"}
 * @editorparams {"name":"autoclose","parameterType":"{mode:'minimize' | 'close' | 'closetime',duration?:number}","description": "用于AI聊天，在提问完成后，设置AI窗口的自动关闭模式。其中 mode 设为 minimize 时窗口会最小化，设为 close 时窗口会直接关闭，设为 closetime 时窗口会根据 duration 配置的值延时关闭。duration配置单位为秒（s），默认值为 3 秒"}
 * @editorparams {"name":"enablenoaccess","parameterType":"boolean","defaultvalue":"false", "description":"是否启用无权限模式，若启用无权限模式，上传文件夹需拼接'$'字符，也不需要计算下载凭证"}
 * @editorparams {"name":"globaldownloadprifix","parameterType":"boolean","defaultvalue":"false", "description":"是否使用全局文件下载前缀，若启用，则以global作为前缀"}
 * @editorparams {"name":"srfaiappendresource","parameterType":"string", "description":"AI聊天默认附加资源数据"}
 * @editorparams {"name":"srfmode","parameterType":"string", "description":"指定AI聊天自定义模式"}
 * @editorparams {"name":"srfaichunkview","parameterType":"string", "description":"知识切片视图，用于定义AI交谈打开目标知识切片视图"}
 * @editorparams {"name":"srfaichunkentity","parameterType":"string", "description":"知识切片实体，用于定义AI交谈打开知识切片视图数据主键key"}
 * @editorparams {"name":"chunkview","parameterType":"string", "description":"切片视图，用于定义打开目标切片视图"}
 * @editorparams {"name":"chunkentity","parameterType":"string", "description":"切片实体，用于打开切片视图数据主键key"}
 * @editorparams {"name":"openmode",parameterType:'default' | 'top' | 'bottom',description: 用于AI聊天，AI窗口的打开模式，default：默认窗口模式；top：顶部飘窗模式；bottom：底部飘窗模式}
 * @editorparams {"name":"containerheight",parameterType:"number",description: 用于AI聊天飘窗模式时的高度，0到100按照百分比，大于100按照像素处理}
 * @editorparams {"name":"srfaichatcaption","parameterType":"string", "description":"自定义AI交谈框标题"}
 * @ignoreprops  autoFocus | overflowMode
 * @ignoreemits  blur | focus | infoTextChange | enter
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IBizMarkDown: any = defineComponent({
  name: 'IBizMarkDown',
  props: getMarkDownProps<MarkDownEditorController>(),
  emits: getEditorEmits(),
  setup(props, { emit }) {
    const ns = useNamespace('markdown');

    const c: MarkDownEditorController | undefined = props.controller;

    const editor = ref<Cherry | null>(null);
    const editorPreview = ref<Cherry | null>(null);

    const id = createUUID();
    const previewId = createUUID();

    // 请求头
    const headers: Ref<IData> = ref({
      [`${ibiz.env.tokenHeader}Authorization`]: `${
        ibiz.env.tokenPrefix
      }Bearer ${getAppCookie(CoreConst.TOKEN)}`,
    });

    // 上传文件路径
    const uploadUrl: Ref<string> = ref('');

    // 下载文件路径
    const downloadUrl: Ref<string> = ref('');

    // 编辑器主题
    const { UIStore } = useUIStore();

    // 编辑器模式
    const defaultModel = ref('editOnly');

    // 预览态预览图片地址
    const previewImage: Ref<string> = ref('');

    // 是否打开编辑态
    const isEditing = ref(false);

    // data响应式变更基础路径
    watch(
      () => props.data,
      newVal => {
        if (newVal && c) {
          const editorParams: IData = {
            ...c.editorParams,
            enableNoAccess: c.enableNoAccess,
            globalDownloadPrifix: c.globalDownloadPrifix,
          };
          if (editorParams.uploadparams) {
            editorParams.uploadParams = JSON.parse(editorParams.uploadparams);
          }
          if (editorParams.exportparams) {
            editorParams.exportParams = JSON.parse(editorParams.exportparams);
          }
          const urls = ibiz.util.file.calcFileUpDownUrl(
            c.context,
            c.params,
            newVal,
            editorParams,
          );
          uploadUrl.value = urls.uploadUrl;
          downloadUrl.value = urls.downloadUrl;
        }
      },
      { immediate: true, deep: true },
    );

    // // 自定义图片上传
    const fileUpload = async (file: Blob, callback: (_url: string) => void) => {
      const data = await ibiz.util.file.fileUpload(
        uploadUrl.value,
        file,
        headers.value,
      );
      let url = downloadUrl.value.replace('%fileId%', data.fileid);
      if (ibiz.config.common.enableDownloadTicket && c && !c.enableNoAccess) {
        const downloadTicket = await ibiz.util.file.getDownloadTicket(
          c.context,
          c.params,
          props.data || {},
          { fileId: data.fileid },
          c.downloadTicketParams,
        );
        if (downloadTicket && downloadTicket.ticket)
          url = downloadUrl.value.replace('%fileId%', downloadTicket.ticket);
      }
      callback(url);
    };

    watch(
      () => props.value,
      (newVal, oldVal) => {
        if (newVal !== oldVal) editorPreview.value?.setMarkdown(newVal, false);
      },
      { immediate: true },
    );

    // 图片加载回调
    const beforeImageMounted = (e: string, src: string) => {
      return { [e]: src };
    };

    /**
     * @description 解析协议
     * @param {string} urlStr
     * @returns {*}  {{
     *       context: IParams;
     *       params: IParams;
     *       id: string;
     *     }}
     */
    const parseProtocol = (
      urlStr: string,
    ): {
      context: IParams;
      params: IParams;
      protocolId: string;
    } => {
      const url = new URL(urlStr);
      const context: IParams = {};
      const params: IParams = {};
      let protocolId = '';
      if (url.searchParams.size > 0) {
        const navCtx = url.searchParams.get('srfnavctx');
        if (navCtx) {
          try {
            Object.assign(context, JSON.parse(navCtx));
          } catch (error) {
            ibiz.log.error(
              ibiz.i18n.t(
                'runtime.utils.openRedirectView.parseSrfnavctxParameter',
                { urlStr },
              ),
              error,
            );
          }
          url.searchParams.delete('srfnavctx');
        }
        url.searchParams.forEach((value, _key) => {
          params[_key] = value;
        });
        // 路径中存在search但searchParams为空，说明浏览器不支持URLSearchParams对象
      } else if (url.search) {
        const searchParams = parseSearchParams(url.search);
        const navCtx: string = searchParams.srfnavctx;
        if (navCtx) {
          try {
            const value = decodeURIComponent(navCtx);
            Object.assign(context, JSON.parse(value));
          } catch (error) {
            ibiz.log.error(
              ibiz.i18n.t(
                'runtime.utils.openRedirectView.parseSrfnavctxParameter',
                { urlStr },
              ),
              error,
            );
          }
          delete searchParams.srfnavctx;
        }
        Object.keys(searchParams).forEach(key => {
          params[key] = searchParams[key];
        });
      }
      // 兼容edge浏览器
      const pathname = url.pathname || url.hostname;
      const rdTagItems = pathname.replace('//', '').split('/');
      // 当只有一个时，为视图标识。当有两个时，第二个是视图标识
      const [appOrViewTag, viewTag] = rdTagItems;
      // 特殊处理action协议
      protocolId =
        url.protocol === 'action:'
          ? `${url.username}@${pathname}`
          : viewTag || appOrViewTag;
      return {
        params,
        context,
        protocolId,
      };
    };

    /**
     * @description 点击预览
     * @param {MouseEvent} event
     * @returns {*}  {void}
     */
    const onClickPreview = (event: MouseEvent): void => {
      const link = (event.target as HTMLElement)?.closest(
        'a[href^="chunkview://"], a[href^="view://"], a[href^="action://"]',
      );
      if (!link) return;
      event.preventDefault();
      let href = link.getAttribute('href');
      if (!href) return;
      const protocols = ['chunkview', 'view', 'action'];
      // 寻找第一个满足条件的前缀
      const match = protocols.find(p => href!.startsWith(`${p}://`));
      const protocol = match || '';
      if (!protocol) return;
      if (protocol === 'chunkview') {
        const chunkView = c?.editorParams.chunkview || props.chunkView;
        const chunkEntity = c?.editorParams.chunkentity || props.chunkEntity;
        if (!chunkView || !chunkEntity) return;
        const chunkID = href.replace('chunkview://', '');
        href = `view://${chunkView}?srfnavctx={"${chunkEntity}":"${chunkID}"}`;
      }
      const { protocolId, context: _context, params } = parseProtocol(href);
      if (!protocolId) return;
      const context: IContext =
        c?.context?.clone() || props.context?.clone() || IBizContext.create({});
      Object.assign(context, _context);
      switch (protocol) {
        case 'chunkview':
        case 'view':
          ibiz.commands.execute(
            OpenAppViewCommand.TAG,
            protocolId,
            Object.assign(context, { srfkeepnull: true }),
            params,
          );
          break;
        case 'action':
          UIActionUtil.exec(
            protocolId,
            {
              event,
              params,
              context,
              data: [props.data || {}],
              view: c?.view || props.view,
              ctrl: c?.ctrl || props.ctrl,
            },
            context.appid || context.srfappid,
          );
          break;
        default:
          break;
      }
    };

    // AI 聊天实例
    let chatInstance: any;

    const handleAIClick = async () => {
      const appDataEntityId = c?.model.appDataEntityId;
      if (!appDataEntityId || !c.deACMode) return;
      const { zIndex } = useUIStore();
      const containerZIndex = zIndex.increment();
      chatInstance = await ibiz.aiChatUtil.getAIChat();
      const { containerOptions, chatOptions } =
        await ibiz.aiChatUtil.getEditorExAIChatParams(
          c.editorParams,
          c.context,
          c.params,
          props.data || {},
          c.deACMode,
          { chatInstance, view: c.view, ctrl: c.ctrl },
        );
      const resourceOptions = await ibiz.aiChatUtil.getAIResourceOptions(
        c.context,
        c.params,
      );
      chatInstance.create({
        resourceOptions,
        containerOptions: {
          // vant组件层级为2000+，此处需添加2000
          zIndex: containerZIndex + 2000,
          ...containerOptions,
        },
        chatOptions: {
          context: { ...c.context },
          params: { ...c.params, srfactag: c.deACMode.codeName },
          appDataEntityId,
          ...chatOptions,
          action: (action: string, message: IChatMessage) => {
            if (action === 'backfill') emit('change', message.realcontent);
          },
        },
      });
    };

    const [AIMenu, customMenus] = initCustomMenu(
      c as MarkDownEditorController,
      {
        emit,
        handleAIClick,
      },
    );

    const editorOpts = {
      value: props.value,
      nameSpace: 'cherry-markdown',
      themeSettings: {
        mainTheme: UIStore.theme,
        codeBlockTheme: UIStore.theme,
      },
      fileUpload,
      emoji: {
        useUnicode: true,
      },
      header: {
        anchorStyle: 'autonumber',
      },
      editor: {
        // 编辑器的高度，默认100%，如果挂载点存在内联设置的height则以内联样式为主
        height: '100%',
        // defaultModel 编辑器初始化后的默认模式，一共有三种模式：1、双栏编辑预览模式；2、纯编辑模式；3、预览模式
        // edit&preview: 双栏编辑预览模式
        // editOnly: 纯编辑模式（没有预览，可通过toolbar切换成双栏或预览模式）
        // previewOnly: 预览模式（没有编辑框，toolbar只显示“返回编辑”按钮，可通过toolbar切换成编辑模式）
        defaultModel: defaultModel.value,
        codemirror: {
          // 是否自动focus 默认为true
          autofocus: false,
          placeholder: c?.placeHolder,
        },
      },
      toolbars: {
        toolbar: [
          'switchModel',
          'bold',
          'italic',
          'strikethrough',
          'header',
          // 'list',
          {
            insert: [
              'image',
              'audio',
              'video',
              'link',
              'hr',
              'br',
              'code',
              'formula',
              'toc',
              'table',
              'pdf',
              'word',
            ],
          },
          'settings',
        ],
        customMenu: {
          ai: AIMenu,
          ...customMenus,
        },
      },
    };

    // 打印工具类打开时无控制器
    if (c && c.chatCompletion) editorOpts.toolbars.toolbar.unshift('ai');

    // 自定义行为项
    if (c && c.extraActions.length > 0) {
      const keys = c.extraActions.map((item: IData) => {
        return item.uiactionId.split('@')[0];
      });
      editorOpts.toolbars.toolbar.push(...keys);
    }

    onMounted(() => {
      const cherryOptions = {
        id: previewId,
        ...editorOpts,
        editor: { defaultModel: 'previewOnly' },
        callback: {
          onClickPreview,
        },
      };
      editorPreview.value = new Cherry(cherryOptions as IData);
    });

    onUnmounted(() => {
      editor.value?.destroy();
      editor.value = null;
      editorPreview.value?.destroy();
      editorPreview.value = null;
    });

    // 计算触发元素类型
    const calcTargetType = (
      target: HTMLElement,
    ): {
      type: string;
      url: string;
    } => {
      const result = {
        type: '',
        url: '',
      };
      if (target.nodeName === 'A') {
        result.type = 'A';
        result.url = (target as HTMLAnchorElement).hash;
      } else if (target.parentNode && target.parentNode.nodeName === 'A') {
        result.type = 'A';
        result.url = (target.parentNode as HTMLAnchorElement).hash;
      } else if (target.nodeName === 'IMG') {
        result.type = 'IMG';
        result.url = (target as HTMLImageElement).src;
      }
      return result;
    };

    const openPicker = async (event: TouchEvent) => {
      // 如果点击项是a标签,并且有href,值为#开头,且后续值为页面内的元素的id,就走页面滚动导航
      // 如果是图片，则进行放大预览
      const { target } = event;
      if (target) {
        const result = calcTargetType(target as HTMLElement);
        if (result.type === 'A' && result.url.startsWith('#')) {
          // 点击a标签，本页面内滑动，阻止其他变化
          event.preventDefault();
          event.stopPropagation();
          const targetid = result.url.slice(1);
          if (targetid) {
            const preview = document.getElementById(previewId);
            if (preview) {
              const anchor = document.getElementById(targetid);
              if (anchor) anchor.scrollIntoView({ behavior: 'smooth' });
            }
            return;
          }
        }
        // 点击图片，展开预览
        if (result.type === 'IMG' && result.url)
          previewImage.value = result.url;
      }
    };

    /**
     * @description 打开编辑态
     */
    const onEdit = (): void => {
      isEditing.value = true;
    };

    /**
     * 处理打开编辑面板
     */
    const handleOpened = (): void => {
      if (editor.value) {
        editor.value.setMarkdown(props.value || '', false);
      } else {
        const cherryOptions = {
          id,
          ...editorOpts,
          callback: {
            onClickPreview,
            beforeImageMounted,
          },
        };
        editor.value = new Cherry(cherryOptions as IData);
        c?.setMDEditor(editor.value);
      }
    };

    // 取消编辑
    const handleCancel = () => {
      if (!editor.value) return;
      editor.value.setMarkdown('', false);
      isEditing.value = false;
    };

    // 确认编辑
    const handleConfirm = () => {
      if (!editor.value) return;
      emit('change', editor.value.getMarkdown());
      editor.value.setMarkdown('', false);
      isEditing.value = false;
    };

    // 关闭图片预览时清除预览图片地址
    const handlePreviewClose = () => {
      previewImage.value = '';
    };

    return {
      ns,
      id,
      editor,
      headers,
      isEditing,
      previewId,
      defaultModel,
      previewImage,
      onEdit,
      openPicker,
      handleOpened,
      handleCancel,
      handleConfirm,
      handlePreviewClose,
    };
  },
  render() {
    return (
      <div
        class={[
          this.ns.b(),
          this.disabled ? this.ns.m('disabled') : '',
          this.readonly ? this.ns.m('readonly') : '',
        ]}
      >
        {!this.readonly && !this.disabled ? (
          <div onClick={this.onEdit} class={this.ns.e('edit')}>
            <ion-icon name='create-outline'></ion-icon>
          </div>
        ) : null}

        <van-field readonly disabled={this.disabled} onClick={this.openPicker}>
          {{
            input: (
              <div
                id={this.previewId}
                class={[this.ns.e('cherry'), this.ns.em('cherry', 'preview')]}
              ></div>
            ),
          }}
        </van-field>

        <iBizPreviewImage
          url={this.previewImage}
          class={this.ns.e('image')}
          onClosed={this.handlePreviewClose}
        ></iBizPreviewImage>

        <van-action-sheet
          teleport='body'
          onOpened={this.handleOpened}
          v-model:show={this.isEditing}
          class={this.ns.e('popup')}
        >
          <div class={this.ns.em('popup', 'content')}>
            <div
              id={this.id}
              class={[this.ns.e('cherry'), this.ns.em('cherry', 'edit')]}
            ></div>
          </div>
          <div class={this.ns.em('popup', 'footer')}>
            <van-button class={this.ns.e('cancel')} onClick={this.handleCancel}>
              {ibiz.i18n.t('editor.common.cancel')}
            </van-button>
            <van-button
              type='primary'
              class={this.ns.e('confirm')}
              onClick={this.handleConfirm}
            >
              {ibiz.i18n.t('editor.common.confirm')}
            </van-button>
          </div>
        </van-action-sheet>
      </div>
    );
  },
});

export default IBizMarkDown;
